/**
 * StoryList — three icon-bullet entries explaining a step's intent.
 *
 * Prototype's `.story-list`. Each item is a fixed-width icon column + a
 * title/description block. Items are typed objects so route code reads as
 * data, not nested JSX.
 */
import * as React from "react";
import { cn } from "@flint/ui";

export interface StoryItem {
  /** Lucide-style stroked SVG node, sized 24×24. */
  icon: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
}

export interface StoryListProps extends React.HTMLAttributes<HTMLUListElement> {
  items: readonly StoryItem[];
}

export function StoryList({ items, className, ...props }: StoryListProps) {
  return (
    <ul className={cn("flex flex-col gap-4 list-none p-0 m-0", className)} {...props}>
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span
            aria-hidden
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-sky-50 text-sky-600"
          >
            {item.icon}
          </span>
          <div className="flex flex-col gap-0.5">
            <strong className="text-sm leading-[18px] font-semibold text-ink">
              {item.title}
            </strong>
            <span className="text-xs leading-[17px] text-muted">{item.description}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}
